import React, { useEffect, useMemo } from "react";
import {
  HiOutlinePaperAirplane,
  HiOutlineShoppingBag,
  HiOutlineUser,
} from "react-icons/hi2";
import { useSearch } from "../../../../contexts/SearchContext";
import { useSelectedResult } from "../../../../contexts/SelectedResultContext";
import { useAppContext } from "../../../../contexts/AppContext";
import { useBookingFlow } from "../hooks/useBookingFlow";
import { buildPlanesSearchSummary } from "../../adapters/flightSearchDisplayAdapter";
import { getDestinationHeroVisual } from "../../adapters/flightDestinationHeroImage";
import {
  countTotalPaxFromRoomInfo,
  buildFlightCardLegRowsFromDeal,
  formatExtrasSegmentDirectionLabel,
  getFlightSegmentsWithLegs,
  getRawLdsDealFareDisplay,
  mapPaxListWithPassengerNames,
  mapRawLdsSupplementsToMockupExtras,
  sumCheckedRawSupplementFares,
} from "../../adapters/flightOfferAdapter";
import {
  buildPricedSelfComposeCombinedOffer,
  computeSelfComposeLivePricing,
  computeSelfComposePricingSsot,
  dealKey,
  formatSelfComposeLegLine,
  formatSelfComposeTotalPaySubline,
  overlayEngineClientOnSelfComposeSsot,
  readEngineClientPerPersonUsd,
} from "../selfCompose/selfComposeSummaryUtils";
import { useSelfComposeUi } from "../selfCompose/SelfComposeUiContext";
import { resolveFlightBaggageDisplayState } from "../../adapters/flightBaggageStepPolicy";
import { BaggageLineIcon } from "../ProposalReviewIcons";
import {
  shouldRequirePassportOnAgentFlightForm,
  validateFlightPassengerPax,
} from "../flightPassengersFormShared";
import { resolveFlightAgentOrderSummaryTotals } from "./resolveFlightAgentOrderSummaryTotals";

function isSelfComposeOffer(offer) {
  return !!(
    offer?.isSelfComposeCombined ||
    offer?.source === "selfCompose"
  );
}

function formatOtaCompactAmount(amount, currency) {
  if (amount == null || !Number.isFinite(Number(amount))) return null;
  const n = Number(amount);
  const rounded =
    Math.abs(n - Math.round(n)) < 0.001 ? Math.round(n) : Math.round(n * 100) / 100;
  const show = String(rounded);
  const c = String(currency || "").trim().toUpperCase();
  if (c === "USD" || c === "$") {
    return `${show}$`;
  }
  if (c === "ILS" || c === "₪") {
    return `₪ ${show}`;
  }
  return c ? `${show} ${c}` : show;
}

function parseLeadingAmount(str) {
  if (!str || typeof str !== "string") return null;
  const m = str.trim().match(/^([\d.,]+)/);
  if (!m) return null;
  const x = parseFloat(m[1].replace(/,/g, ""));
  return Number.isFinite(x) ? x : null;
}

function formatIlsAmount(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n) || n < 0) return "₪ 0";
  const hasFraction = Math.abs(n - Math.round(n)) > 0.001;
  return `₪ ${n.toLocaleString("he-IL", {
    minimumFractionDigits: hasFraction ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
}

function formatUsdAmount(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n) || n < 0) return "$0";
  const hasFraction = Math.abs(n - Math.round(n)) > 0.001;
  return `$${n.toLocaleString("en-US", {
    minimumFractionDigits: hasFraction ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
}

function formatIlsHelperAmount(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n) || n < 0) return "(₪0)";
  // Keep public offer ILS display aligned with agent-side order summary (ceil policy).
  const roundedUp = Math.ceil(n - 1e-9);
  return `(₪${roundedUp.toLocaleString("he-IL")})`;
}

function firstFiniteNumber(...candidates) {
  for (const candidate of candidates) {
    const value = Number(candidate);
    if (Number.isFinite(value)) return value;
  }
  return null;
}

function firstPositiveFiniteNumber(...candidates) {
  for (const candidate of candidates) {
    const value = Number(candidate);
    if (Number.isFinite(value) && value > 0) return value;
  }
  return null;
}

function formatCheckedAtTimestamp(date = new Date()) {
  return new Intl.DateTimeFormat("he-IL", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
}

function translateSupplementTitleHe(rawTitle) {
  const text = String(rawTitle || "").trim();
  if (!text) return "כבודה / תוספות";
  return text
    .replace(/\bBAGGAGE\b/gi, "כבודה")
    .replace(/\bEXTRAS?\b/gi, "תוספות")
    .replace(/\bTROLLEY\b/gi, "טרולי")
    .replace(/\bCABIN\s*BAG\b/gi, "כבודת יד")
    .replace(/\bHAND\s*BAG\b/gi, "כבודת יד")
    .replace(/\bCHECK[\s-]*IN\s*BAG\b/gi, "כבודה לבטן המטוס")
    .replace(/\bSEAT\b/gi, "מושב")
    .replace(/\s{2,}/g, " ")
    .trim();
}

function isPassengerInfoComplete(
  passenger = {},
  index = 0,
  { requirePassport = true } = {}
) {
  return validateFlightPassengerPax(passenger, index, { requirePassport });
}

/**
 * בכותרת מקטע (הלוך/חזור) מסירים קידומת כיוון כדי לא לשכפל אותה בטקסט.
 */
function stripDirPrefixInSegmentedList(title) {
  return String(title ?? "")
    .replace(/^(?:הלוך|חזור):\s*/u, "")
    .trim();
}

/** תיאור שדרוג: קידומת RTL + גוף באנגלית ב־LTR (מניעת ערבוב בכיווניות) */
function UpgradeTitleText({ title }) {
  const raw = String(title ?? "");
  const i = raw.indexOf(":");
  if (i === -1) {
    return (
      <span className="fw-live-summary__upgrades-en" dir="ltr" translate="no">
        {raw}
      </span>
    );
  }
  return (
    <span className="fw-live-summary__upgrades-name-mixed">
      <span className="fw-live-summary__upgrades-dirlabel">
        {raw.slice(0, i)}:
      </span>{" "}
      <span className="fw-live-summary__upgrades-en" dir="ltr" translate="no">
        {raw.slice(i + 1).trim()}
      </span>
    </span>
  );
}

export default function FlightWizardLiveSummary({
  compact = false,
  layoutVariant = "full", // full | priceOnly | detailsOnly
}) {
  const { airlines } = useAppContext();
  const {
    origin,
    location,
    calendarDateArrival,
    calendarDateLeave,
    roomInfo,
    flightTripType,
    savedDeal,
    selectedSelfComposeOutboundDeal,
    selectedSelfComposeInboundDeal,
    attractionsSearchResults,
    calculateLDSDealFareForRoomsComposition,
  } = useSearch();
  const { ldsSupplement, handleFeeData, usdToIls } = useSelectedResult();
  const {
    selectedFlightOffer,
    extras,
    passengers,
    bookingStatus,
    draftStatus,
    paymentStatus,
    currentStep,
    pricing,
    nextStep,
    canAdvanceFromCurrentStep,
  } = useBookingFlow();
  const { buildPhase } = useSelfComposeUi();
  const hideLiveSummaryCta =
    currentStep === "flightDetails" ||
    currentStep === "review" ||
    (currentStep === "selfComposePick" &&
      buildPhase === "summary" &&
      !selectedFlightOffer?.isSelfComposeCombined);

  const searchSummary = useMemo(
    () =>
      buildPlanesSearchSummary({
        origin,
        location,
        calendarDateArrival,
        calendarDateLeave,
        roomInfo,
      }),
    [
      origin,
      location,
      calendarDateArrival,
      calendarDateLeave,
      roomInfo,
    ]
  );

  const destinationHero = useMemo(
    () =>
      getDestinationHeroVisual(location, searchSummary.destinationLabel),
    [location, searchSummary.destinationLabel]
  );

  const rawDeal = selectedFlightOffer?.rawDeal ?? savedDeal ?? null;
  const isProposalReview = currentStep === "review";
  const baggageDisplayState = useMemo(
    () => resolveFlightBaggageDisplayState(rawDeal),
    [rawDeal]
  );
  const baseDisplay = useMemo(
    () => (rawDeal ? getRawLdsDealFareDisplay(rawDeal) : null),
    [rawDeal]
  );
  const providerCurrency =
    pricing?.currency ||
    (pricing?.total != null && Number.isFinite(Number(pricing.total))
      ? "ILS"
      : baseDisplay?.rawCurrency || "");
  const baggageRaw = useMemo(
    () => sumCheckedRawSupplementFares(ldsSupplement),
    [ldsSupplement]
  );
  const ilsPerUsdRate = useMemo(() => {
    const r = usdToIls(1);
    return Number.isFinite(r) && r > 0 ? r : null;
  }, [usdToIls]);
  const totalRaw =
    baseDisplay?.rawTotalFare != null
      ? baseDisplay.rawTotalFare + baggageRaw
      : null;

  const outLegDeal =
    selectedFlightOffer?.selfComposeLegs?.outboundRawDeal ??
    selectedSelfComposeOutboundDeal ??
    null;
  const inLegDeal =
    selectedFlightOffer?.selfComposeLegs?.inboundRawDeal ??
    selectedSelfComposeInboundDeal ??
    null;

  const showSelfComposeLegRows =
    currentStep === "selfComposePick" ||
    isSelfComposeOffer(selectedFlightOffer);

  const pricedSelfComposeOffer = useMemo(() => {
    if (!showSelfComposeLegRows) return null;
    if (outLegDeal && inLegDeal) {
      const rebuilt = buildPricedSelfComposeCombinedOffer(
        outLegDeal,
        inLegDeal,
        roomInfo,
        calculateLDSDealFareForRoomsComposition,
        {
          baggageRaw,
          passengersLength: passengers.length > 0 ? passengers.length : 0,
        }
      );
      if (readEngineClientPerPersonUsd(rebuilt) != null) return rebuilt;
    }
    if (
      isSelfComposeOffer(selectedFlightOffer) &&
      selectedFlightOffer?.selfComposePricingSsot?.source === "engine" &&
      readEngineClientPerPersonUsd(selectedFlightOffer) != null
    ) {
      return selectedFlightOffer;
    }
    return null;
  }, [
    showSelfComposeLegRows,
    selectedFlightOffer,
    outLegDeal,
    inLegDeal,
    roomInfo,
    calculateLDSDealFareForRoomsComposition,
    baggageRaw,
    passengers.length,
  ]);

  const rawSelfComposeSsot = useMemo(() => {
    if (!outLegDeal && !inLegDeal) return null;
    return computeSelfComposePricingSsot(
      outLegDeal,
      inLegDeal,
      roomInfo,
      baggageRaw,
      passengers.length > 0 ? passengers.length : 0
    );
  }, [
    outLegDeal,
    inLegDeal,
    roomInfo,
    baggageRaw,
    passengers.length,
  ]);

  const selfComposeSsot = useMemo(
    () => overlayEngineClientOnSelfComposeSsot(rawSelfComposeSsot, pricedSelfComposeOffer),
    [rawSelfComposeSsot, pricedSelfComposeOffer]
  );

  const selfComposeFlightSsot = useMemo(() => {
    if (!showSelfComposeLegRows || (!outLegDeal && !inLegDeal)) return null;
    const raw = computeSelfComposePricingSsot(
      outLegDeal,
      inLegDeal,
      roomInfo,
      0,
      passengers.length > 0 ? passengers.length : 0
    );
    return overlayEngineClientOnSelfComposeSsot(raw, pricedSelfComposeOffer);
  }, [
    showSelfComposeLegRows,
    outLegDeal,
    inLegDeal,
    roomInfo,
    passengers.length,
    pricedSelfComposeOffer,
  ]);

  const supplierSelfComposePerPaxUsd = useMemo(() => {
    if (!selfComposeFlightSsot?.currency) return null;
    const cur = String(selfComposeFlightSsot.currency).trim().toUpperCase();
    const paxN = Math.max(1, selfComposeFlightSsot.passengerCount || 1);
    const perInSupplier = selfComposeFlightSsot.flightsTotal / paxN;
    if (!Number.isFinite(perInSupplier)) return null;
    if (cur === "USD" || cur === "$") return perInSupplier;
    if (
      (cur === "ILS" || cur === "₪") &&
      ilsPerUsdRate != null &&
      ilsPerUsdRate > 0
    ) {
      return perInSupplier / ilsPerUsdRate;
    }
    return null;
  }, [selfComposeFlightSsot, ilsPerUsdRate]);

  const liveComposePricing = useMemo(() => {
    if (!showSelfComposeLegRows) {
      return { perPersonStr: null, totalStr: null };
    }
    if (!outLegDeal && !inLegDeal) {
      return { perPersonStr: null, totalStr: null };
    }
    return computeSelfComposeLivePricing(
      outLegDeal,
      inLegDeal,
      roomInfo,
      baggageRaw,
      passengers.length > 0 ? passengers.length : 0
    );
  }, [
    showSelfComposeLegRows,
    outLegDeal,
    inLegDeal,
    roomInfo,
    baggageRaw,
    passengers.length,
  ]);

  const outboundLegLine = useMemo(
    () => formatSelfComposeLegLine(outLegDeal, roomInfo),
    [outLegDeal, roomInfo]
  );
  const inboundLegLine = useMemo(
    () => formatSelfComposeLegLine(inLegDeal, roomInfo),
    [inLegDeal, roomInfo]
  );

  const routeLabel =
    selectedFlightOffer?.route ||
    (searchSummary.originLabel &&
    searchSummary.destinationLabel &&
    searchSummary.originLabel !== "—" &&
    searchSummary.destinationLabel !== "—"
      ? `${searchSummary.originLabel} → ${searchSummary.destinationLabel}`
      : "—");

  const routeCompact = useMemo(() => {
    if (!routeLabel || routeLabel === "—") return "—";
    return routeLabel.replace(/\s*→\s*/g, " ↔ ");
  }, [routeLabel]);

  const datesLine = useMemo(() => {
    const dep = searchSummary.departureDate;
    const ret = searchSummary.returnDate;
    if (!ret || ret === "—") return dep;
    return `${dep} – ${ret}`;
  }, [searchSummary.departureDate, searchSummary.returnDate]);

  /**
   * ספירת נוסעים להצגת מחיר: חיפוש (חדרים — מבוגרים+ילדים+תינוקות) → טופס נוסעים → SSOT בהרכבה.
   */
  const passengersCountFromSearch = useMemo(
    () => countTotalPaxFromRoomInfo(roomInfo),
    [roomInfo]
  );

  const effectivePaxForUi = useMemo(() => {
    if (passengersCountFromSearch != null && passengersCountFromSearch > 0) {
      return passengersCountFromSearch;
    }
    if (passengers.length > 0) return passengers.length;
    const ssotPax = selfComposeSsot?.passengerCount;
    if (typeof ssotPax === "number" && ssotPax > 0) return ssotPax;
    return 1;
  }, [
    passengersCountFromSearch,
    passengers.length,
    selfComposeSsot?.passengerCount,
  ]);

  const perPersonLabel =
    (selfComposeSsot?.currency
      ? `${selfComposeSsot.totalPerPerson} ${selfComposeSsot.currency}`
      : null) ||
    liveComposePricing.perPersonStr ||
    selectedFlightOffer?.pricePerPersonDisplayLabel ||
    selectedFlightOffer?.priceDisplayLabel ||
    baseDisplay?.priceDisplayLabel ||
    "—";

  const totalLabel =
    (selfComposeSsot
      ? formatSelfComposeTotalPaySubline(selfComposeSsot)
      : null) ||
    (liveComposePricing.totalStr != null
      ? liveComposePricing.totalStr
      : null) ||
    (totalRaw != null && Number.isFinite(totalRaw)
      ? providerCurrency
        ? `${totalRaw} ${providerCurrency}`
        : String(totalRaw)
      : null) ||
    baseDisplay?.priceDisplayLabel ||
    "—";

  const selfComposeTotalPayLine = useMemo(
    () =>
      selfComposeSsot ? formatSelfComposeTotalPaySubline(selfComposeSsot) : null,
    [selfComposeSsot]
  );

  const tripBadge =
    flightTripType === "OneWay"
      ? "כיוון אחד"
      : flightTripType === "RoundTrip"
        ? "הלוך-חזור"
        : null;

  const paxLine =
    passengers.length > 0
      ? `${passengers.length} נוסעים (טופס)`
      : searchSummary.passengersLabel;

  const hasFlightSelection = !!(
    selectedFlightOffer ||
    savedDeal ||
    rawDeal ||
    outLegDeal ||
    inLegDeal
  );

  /**
   * SSOT: המחיר הסופי נקרא *רק* מ-BookingFlowContext.pricing.total. ערך זה כולל
   * base + handlingFee + creditCardFee + extras, ומעודכן סינכרונית מתוך פעולות הפלואו.
   * Fallbacks נשמרים רק למצבי קצה נדירים (state חלקי/טעינה).
   */
  const totalPayAmount = useMemo(() => {
    const p = pricing?.total;
    if (p != null && Number.isFinite(p) && p > 0) return p;
    if (totalRaw != null && Number.isFinite(totalRaw) && totalRaw > 0)
      return totalRaw;
    return parseLeadingAmount(
      typeof totalLabel === "string" ? totalLabel : String(totalLabel ?? "")
    );
  }, [pricing?.total, totalRaw, totalLabel]);

  /**
   * מחיר לאדם לתצוגה (SSOT / הרכבה חיה / fallback מספרי מ-offer) — לא לשימוש ישיר במסלול RT/OW סטנדרטי.
   */
  const perPersonAmount = useMemo(() => {
    if (
      selfComposeSsot != null &&
      Number.isFinite(selfComposeSsot.totalPerPerson)
    ) {
      return selfComposeSsot.totalPerPerson;
    }
    const parsedCompose = parseLeadingAmount(liveComposePricing.perPersonStr);
    if (parsedCompose != null && Number.isFinite(parsedCompose)) {
      return parsedCompose;
    }
    if (
      selectedFlightOffer?.pricePerPerson != null &&
      Number.isFinite(selectedFlightOffer.pricePerPerson)
    ) {
      return selectedFlightOffer.pricePerPerson;
    }
    return null;
  }, [
    selfComposeSsot,
    liveComposePricing.perPersonStr,
    selectedFlightOffer?.pricePerPerson,
  ]);

  /** RT/OW (לא SSOT הרכבה): ממוצע לנוסע = סה״כ / מספר נוסעים — בלי הסתמכות על adultFare כשהוא שווה ל-total */
  const standardDerivedPerPerson = useMemo(() => {
    if (selfComposeSsot != null) return null;
    const n = effectivePaxForUi;
    if (n <= 1) return null;
    if (
      totalPayAmount == null ||
      !Number.isFinite(totalPayAmount) ||
      totalPayAmount <= 0
    ) {
      return null;
    }
    return totalPayAmount / n;
  }, [selfComposeSsot, effectivePaxForUi, totalPayAmount]);

  const totalPayFormatted = useMemo(() => {
    if (!hasFlightSelection) return null;
    const fmt = formatOtaCompactAmount(totalPayAmount, providerCurrency);
    if (fmt) return fmt;
    return totalLabel !== "—" ? totalLabel : null;
  }, [
    hasFlightSelection,
    totalPayAmount,
    providerCurrency,
    totalLabel,
  ]);

  const perPersonFormatted = useMemo(() => {
    if (!hasFlightSelection) return null;
    const fmt = formatOtaCompactAmount(perPersonAmount, providerCurrency);
    if (fmt) return fmt;
    return perPersonLabel !== "—" ? perPersonLabel : null;
  }, [
    hasFlightSelection,
    perPersonAmount,
    providerCurrency,
    perPersonLabel,
  ]);

  const standardPerPersonFormatted = useMemo(() => {
    if (!hasFlightSelection || standardDerivedPerPerson == null) return null;
    const fmt = formatOtaCompactAmount(
      standardDerivedPerPerson,
      providerCurrency
    );
    if (fmt) return fmt;
    return String(standardDerivedPerPerson);
  }, [hasFlightSelection, standardDerivedPerPerson, providerCurrency]);

  /**
   * שורת "לאדם" אחרי סה״כ (RT/OW): רק מעל נוסע אחד, ממוצע עקבי עם הסה״כ, והפרש אמיתי בין סה״כ לממוצע.
   */
  const showStandardPerPersonSubline = useMemo(() => {
    if (!hasFlightSelection || selfComposeSsot != null) return false;
    const n = effectivePaxForUi;
    if (n <= 1) return false;
    if (
      standardDerivedPerPerson == null ||
      !Number.isFinite(standardDerivedPerPerson)
    ) {
      return false;
    }
    if (
      totalPayAmount == null ||
      !Number.isFinite(totalPayAmount) ||
      totalPayAmount <= 0
    ) {
      return false;
    }
    const implied = standardDerivedPerPerson * n;
    const reconTol = 0.02 * n + 0.05;
    if (Math.abs(totalPayAmount - implied) > reconTol) return false;
    return Math.abs(totalPayAmount - standardDerivedPerPerson) > 0.02;
  }, [
    hasFlightSelection,
    selfComposeSsot,
    effectivePaxForUi,
    standardDerivedPerPerson,
    totalPayAmount,
  ]);

  const canContinue = canAdvanceFromCurrentStep();
  const flowDone =
    bookingStatus === "confirmed" ||
    currentStep === "success";

  useEffect(() => {
    if (process.env.NODE_ENV === "production") return;
    if (!selfComposeSsot || !isSelfComposeOffer(selectedFlightOffer)) return;
    const pax = Math.max(1, selfComposeSsot.passengerCount || 1);
    const expectedPer = selfComposeSsot.totalBooking / pax;
    if (Math.abs(selfComposeSsot.totalPerPerson - expectedPer) > 0.02) {
      console.warn(
        "[FlightWizardLiveSummary] SSOT invariant: totalPerPerson !== totalBooking / pax",
        {
          totalPerPerson: selfComposeSsot.totalPerPerson,
          totalBooking: selfComposeSsot.totalBooking,
          pax,
          expectedPer,
        }
      );
    }
    const engineUsd = readEngineClientPerPersonUsd(pricedSelfComposeOffer);
    if (engineUsd != null && selfComposeSsot.source === "engine") {
      if (Math.abs(Number(engineUsd) - selfComposeSsot.totalPerPerson) > 0.02) {
        console.warn(
          "[FlightWizardLiveSummary] Engine priceClientPerPersonUsd differs from SSOT",
          { engineUsd, ssotPerPerson: selfComposeSsot.totalPerPerson }
        );
      }
    } else {
      const pp = selectedFlightOffer?.pricePerPerson;
      if (pp != null && Number.isFinite(Number(pp))) {
        if (Math.abs(Number(pp) - selfComposeSsot.totalPerPerson) > 0.02) {
          console.warn(
            "[FlightWizardLiveSummary] Combined offer pricePerPerson differs from SSOT",
            { offerPerPerson: pp, ssotPerPerson: selfComposeSsot.totalPerPerson }
          );
        }
      }
    }
  }, [selectedFlightOffer, selfComposeSsot, pricedSelfComposeOffer]);

  const cardLegRows = useMemo(
    () => (rawDeal ? buildFlightCardLegRowsFromDeal(rawDeal) : []),
    [rawDeal]
  );

  const supplementRowsByKey = useMemo(() => {
    const rows = rawDeal ? mapRawLdsSupplementsToMockupExtras(rawDeal) : [];
    return new Map(rows.map((r) => [r.supplementKey, r]));
  }, [rawDeal]);

  const extrasSegmentCount = useMemo(() => {
    if (!rawDeal) return 1;
    const n = getFlightSegmentsWithLegs(rawDeal).length;
    return n > 0 ? n : 1;
  }, [rawDeal]);

  const selectedUpgrades = useMemo(() => {
    const sel = extras?.supplementPaxSelections;
    const hasIlsFareMap =
      providerCurrency === "ILS" &&
      extras?.supplementUnitFaresIls &&
      Object.keys(extras.supplementUnitFaresIls).length > 0;
    const fares = hasIlsFareMap
      ? extras.supplementUnitFaresIls
      : extras?.supplementUnitFares;
    if (!sel || typeof sel !== "object") return [];
    const out = [];
    for (const [key, ids] of Object.entries(sel)) {
      if (!Array.isArray(ids) || ids.length === 0) continue;
      const row = supplementRowsByKey.get(key);
      const baseTitle = row?.titleLine ?? "שדרוג";
      const dir = formatExtrasSegmentDirectionLabel(
        row?.segmentIndex ?? 0,
        extrasSegmentCount
      );
      const title = dir ? `${dir}: ${baseTitle}` : baseTitle;
      const unit = fares?.[key];
      const cur = hasIlsFareMap ? "ILS" : row?.currency || providerCurrency;
      let plusStr = null;
      if (Number.isFinite(unit) && unit > 0) {
        const lineTotal = unit * ids.length;
        const fmt = formatOtaCompactAmount(lineTotal, cur);
        if (fmt) plusStr = `+${fmt}`;
      }
      out.push({
        key,
        title,
        count: ids.length,
        plusStr,
      });
    }
    return out;
  }, [extras, supplementRowsByKey, providerCurrency, extrasSegmentCount]);

  const bookingTimelineRows = useMemo(() => {
    if (!Array.isArray(cardLegRows) || cardLegRows.length === 0) return [];
    return cardLegRows.map((row, idx) => ({
      id: row.id || `timeline-${idx}`,
      label:
        row.labelHe ||
        (idx === 0 ? "הלוך" : idx === 1 ? "חזור" : `קטע ${idx + 1}`),
      dep: row.departureTime || "--:--",
      arr: row.arrivalTime || "--:--",
      from: row.originCode || "—",
      to: row.destinationCode || "—",
      duration: row.duration || "—",
      stopLabel: row.stopLabelHe || "—",
      airlineCode: row.airlineCode || "",
      depDateDisplay: row.depDateDisplay || "—",
    }));
  }, [cardLegRows]);

  const airlineMeta = useMemo(() => {
    const code =
      bookingTimelineRows.find((row) => row.airlineCode)?.airlineCode ||
      null;
    if (!code) return { code: null, name: "—", logo: null };
    const normalizedCode = String(code).trim();
    const airline = Array.isArray(airlines)
      ? airlines.find(
          (a) => String(a?.code || "").toUpperCase() === normalizedCode.toUpperCase()
        )
      : null;
    return {
      code: normalizedCode,
      name: normalizedCode,
      logo: airline?.logo || null,
    };
  }, [bookingTimelineRows, airlines]);

  const requirePassport = shouldRequirePassportOnAgentFlightForm(
    selectedFlightOffer
  );
  const passengerRows = useMemo(() => {
    if (!Array.isArray(passengers) || passengers.length === 0) return [];
    return passengers.map((p, idx) => ({
      id: `passenger-${idx}`,
      name:
        `${String(p.firstName || "").trim()} ${String(p.lastName || "").trim()}`.trim() ||
        `נוסע ${idx + 1}`,
      complete: isPassengerInfoComplete(p, idx, { requirePassport }),
    }));
  }, [passengers, requirePassport]);

  const pricingExtras = Number(pricing?.extras);
  const baggageIlsTotal =
    Number.isFinite(pricingExtras) && pricingExtras > 0
      ? pricingExtras
      : Number(baggageRaw) > 0
        ? Number(baggageRaw)
        : 0;
  const baggageAmount = baggageIlsTotal;
  const tracePricing = selectedFlightOffer?.priceTrace || null;
  const engineSelfComposePerPaxUsd = readEngineClientPerPersonUsd(
    pricedSelfComposeOffer
  );
  const displayedSelfComposePerPaxUsd =
    engineSelfComposePerPaxUsd != null
      ? Math.round(engineSelfComposePerPaxUsd)
      : null;
  const perPaxUsd = showSelfComposeLegRows
    ? firstFiniteNumber(
        displayedSelfComposePerPaxUsd,
        engineSelfComposePerPaxUsd,
        !outLegDeal || !inLegDeal ? supplierSelfComposePerPaxUsd : null
      )
    : firstFiniteNumber(
        tracePricing?.finalClientUsdPerPerson,
        selectedFlightOffer?.agentNetUsdPerPax,
        selectedFlightOffer?.priceClientPerPersonUsd
      );
  const pax = Math.max(1, Number(effectivePaxForUi) || 1);
  const usdRateUsed = firstPositiveFiniteNumber(
    showSelfComposeLegRows
      ? pricedSelfComposeOffer?.priceTrace?.usdRateUsed
      : null,
    showSelfComposeLegRows ? pricedSelfComposeOffer?.usdRateUsed : null,
    tracePricing?.usdRateUsed,
    selectedFlightOffer?.usdRateUsed,
    ilsPerUsdRate
  );
  const baseTotalUsd = perPaxUsd != null ? perPaxUsd * pax : null;
  const agentNetUsdTotal = baseTotalUsd;
  const agentNetIls =
    agentNetUsdTotal != null && usdRateUsed != null ? agentNetUsdTotal * usdRateUsed : null;
  const baggageUsdTotal =
    baggageIlsTotal > 0 && Number.isFinite(usdRateUsed) && Number(usdRateUsed) > 0
      ? baggageIlsTotal / Number(usdRateUsed)
      : 0;
  const baggageUsdPerPax =
    baggageUsdTotal > 0 && pax > 0 ? baggageUsdTotal / pax : 0;
  const subtotalUsd =
    Number.isFinite(baseTotalUsd) ? baseTotalUsd + (baggageUsdTotal || 0) : null;
  const subtotalIls =
    Number.isFinite(agentNetIls) ? agentNetIls + (baggageIlsTotal || 0) : null;
  const agentFeeInputType =
    handleFeeData?.type === "%" || handleFeeData?.type === "+"
      ? handleFeeData.type
      : "+";
  const orderSummaryTotals = resolveFlightAgentOrderSummaryTotals({
    pricing,
    handleFeeData,
    usdRate: usdRateUsed ?? ilsPerUsdRate,
    pax,
    selfComposeClientPay:
      showSelfComposeLegRows &&
      outLegDeal &&
      inLegDeal &&
      engineSelfComposePerPaxUsd != null
        ? {
            perPaxUsd: engineSelfComposePerPaxUsd,
            perPaxIls: firstPositiveFiniteNumber(
              pricedSelfComposeOffer?.priceClientPerPersonIls
            ),
            extrasIls: baggageIlsTotal,
          }
        : null,
  });
  const ssotHasPayable =
    orderSummaryTotals.totalIls != null &&
    Number.isFinite(orderSummaryTotals.totalIls) &&
    orderSummaryTotals.totalIls > 0;
  const rawAgentFeeInputValue = Number(handleFeeData?.value);
  const normalizedAgentFeeInputValue =
    Number.isFinite(rawAgentFeeInputValue) && rawAgentFeeInputValue > 0
      ? rawAgentFeeInputValue
      : 0;
  const agentFeeInputIls = ssotHasPayable
    ? orderSummaryTotals.handleFeeIls
    : agentFeeInputType === "%"
      ? Number.isFinite(subtotalIls)
        ? (subtotalIls * normalizedAgentFeeInputValue) / 100
        : null
      : normalizedAgentFeeInputValue;
  const agentFeeUsd = ssotHasPayable
    ? orderSummaryTotals.handleFeeUsd
    : Number.isFinite(agentFeeInputIls) &&
        Number.isFinite(usdRateUsed) &&
        Number(usdRateUsed) > 0
      ? Number(agentFeeInputIls) / Number(usdRateUsed)
      : normalizedAgentFeeInputValue <= 0
        ? 0
        : null;
  const finalClientUsd = ssotHasPayable
    ? orderSummaryTotals.totalUsd
    : Number.isFinite(subtotalUsd)
      ? Number(subtotalUsd) +
        (Number.isFinite(agentFeeUsd) ? Number(agentFeeUsd) : 0)
      : null;
  const finalClientIls = ssotHasPayable
    ? orderSummaryTotals.totalIls
    : Number.isFinite(subtotalIls) && Number.isFinite(agentFeeInputIls)
      ? Number(subtotalIls) + Number(agentFeeInputIls)
      : null;
  const displayPerPaxUsd =
    ssotHasPayable && orderSummaryTotals.baseUsd != null
      ? orderSummaryTotals.baseUsd / pax
      : perPaxUsd;
  const displayBaseTotalUsd =
    ssotHasPayable && orderSummaryTotals.baseUsd != null
      ? orderSummaryTotals.baseUsd
      : baseTotalUsd;
  const displayBaseIls =
    ssotHasPayable && orderSummaryTotals.baseIls != null
      ? orderSummaryTotals.baseIls
      : agentNetIls;
  const displaySubtotalUsd =
    ssotHasPayable && orderSummaryTotals.baseUsd != null
      ? Number(orderSummaryTotals.baseUsd) + (baggageUsdTotal || 0)
      : subtotalUsd;
  const displaySubtotalIls =
    ssotHasPayable && orderSummaryTotals.baseIls != null
      ? Number(orderSummaryTotals.baseIls) + (baggageIlsTotal || 0)
      : subtotalIls;

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (!showSelfComposeLegRows || (!outLegDeal && !inLegDeal)) return;
    console.log("[SELF_COMPOSE_PRICE_TRACE]", {
      step: currentStep,
      buildPhase,
      selectedOutboundId: dealKey(outLegDeal),
      selectedInboundId: dealKey(inLegDeal),
      outboundPriceSource: selfComposeSsot?.outboundPriceSource ?? null,
      inboundPriceSource: selfComposeSsot?.inboundPriceSource ?? null,
      calculatedFlightsSubtotal: selfComposeSsot?.flightsTotal ?? null,
      calculatedSubtotal: selfComposeSsot?.totalBooking ?? null,
      passengers: selfComposeSsot?.passengerCount ?? null,
      currency: selfComposeSsot?.currency ?? null,
      supplierPerPaxUsd: supplierSelfComposePerPaxUsd,
      finalTotalUsd: finalClientUsd,
      pricingContextTotal: pricing?.total ?? null,
      pricingSource: pricing?.source ?? null,
    });
  }, [
    showSelfComposeLegRows,
    outLegDeal,
    inLegDeal,
    selfComposeSsot,
    currentStep,
    buildPhase,
    supplierSelfComposePerPaxUsd,
    finalClientUsd,
    pricing?.total,
    pricing?.source,
  ]);

  const hasAgentFee = Number.isFinite(agentFeeInputIls) && agentFeeInputIls > 0.009;
  const hasBaggageOrExtras = Number.isFinite(baggageUsdTotal) && baggageUsdTotal > 0.009;
  const paxListForSummary = useMemo(
    () => mapPaxListWithPassengerNames({ roomInfo, passengers }),
    [roomInfo, passengers]
  );
  const paxNameById = useMemo(
    () => new Map(paxListForSummary.map((p) => [p.id, p.labelHe])),
    [paxListForSummary]
  );
  const baggageBreakdownLines = useMemo(() => {
    if (!hasBaggageOrExtras) return [];
    const selections = extras?.supplementPaxSelections;
    if (!selections || typeof selections !== "object") return [];
    const grouped = new Map();
    for (const [supplementKey, idsRaw] of Object.entries(selections)) {
      const ids = Array.isArray(idsRaw) ? idsRaw.filter(Boolean) : [];
      if (ids.length === 0) continue;
      const row = supplementRowsByKey.get(supplementKey);
      if (!row) continue;
      const segFromKeyMatch = String(supplementKey).match(/^sk_s(\d+)_/i);
      const segmentIndexFromKey =
        segFromKeyMatch && Number.isFinite(Number(segFromKeyMatch[1]))
          ? Number(segFromKeyMatch[1])
          : null;
      const direction =
        formatExtrasSegmentDirectionLabel(
          segmentIndexFromKey != null ? segmentIndexFromKey : row?.segmentIndex ?? 0,
          extrasSegmentCount
        ) ||
        "כיוון";
      const product = translateSupplementTitleHe(row?.titleLine || row?.type || "כבודה");
      const groupKey = `${direction}::${product}`;
      const existing = grouped.get(groupKey) || {
        direction,
        product,
        qty: 0,
        names: [],
      };
      existing.qty += ids.length;
      existing.names.push(
        ...ids.map((id) => paxNameById.get(id) || String(id)).filter(Boolean)
      );
      grouped.set(groupKey, existing);
    }
    return Array.from(grouped.values()).map((row) => ({
      ...row,
      names: [...new Set(row.names)],
    }));
  }, [
    hasBaggageOrExtras,
    extras?.supplementPaxSelections,
    supplementRowsByKey,
    extrasSegmentCount,
    paxNameById,
  ]);
  const usdRateCheckedAt = useMemo(() => formatCheckedAtTimestamp(), []);
  const baggageExtrasLabelHe = useMemo(() => {
    if (!hasBaggageOrExtras) return "כבודה / תוספות";
    if (!Array.isArray(selectedUpgrades) || selectedUpgrades.length === 0) {
      return "כבודה / תוספות";
    }
    const uniqueTitles = [
      ...new Set(
        selectedUpgrades
          .map((u) => translateSupplementTitleHe(u?.title))
          .filter(Boolean)
      ),
    ];
    if (uniqueTitles.length === 1) return uniqueTitles[0];
    if (uniqueTitles.length <= 2) return uniqueTitles.join(" + ");
    return `${uniqueTitles.slice(0, 2).join(" + ")} +${uniqueTitles.length - 2}`;
  }, [hasBaggageOrExtras, selectedUpgrades]);

  useEffect(() => {
    if (process.env.NODE_ENV === "production") return;
    if (!hasFlightSelection) return;
    console.log("[FLIGHT_DETAILS_PRICE_SUMMARY_DISPLAY_AUDIT]", {
      agentNetUsdTotal,
      agentNetUsdPerPax: perPaxUsd,
      pax,
      agentFeeUsd,
      finalClientUsd,
      usdRateUsed,
      ilsHelper: finalClientIls,
    });
  }, [
    hasFlightSelection,
    agentNetUsdTotal,
    perPaxUsd,
    pax,
    agentFeeUsd,
    finalClientUsd,
    usdRateUsed,
    finalClientIls,
  ]);

  useEffect(() => {
    if (process.env.NODE_ENV === "production") return;
    if (!hasFlightSelection) return;
    console.log("[FLIGHT_AGENT_FEE_CONVERSION_DEBUG]", {
      agentNetUsd: agentNetUsdTotal,
      agentNetIls,
      usdRateUsed,
      agentFeeInputIls,
      agentFeeInputType,
      agentFeeUsd,
      finalClientUsd,
      finalClientIls,
    });
  }, [
    hasFlightSelection,
    agentNetUsdTotal,
    agentNetIls,
    usdRateUsed,
    agentFeeInputIls,
    agentFeeInputType,
    agentFeeUsd,
    finalClientUsd,
    finalClientIls,
  ]);

  useEffect(() => {
    if (process.env.NODE_ENV === "production") return;
    if (!hasFlightSelection) return;
    console.log("[FLIGHT_PRICE_SUMMARY_BREAKDOWN_DEBUG]", {
      perPaxUsd,
      pax,
      baseTotalUsd,
      baggageUsdPerPax,
      baggageUsdTotal,
      agentFeeUsd,
      finalClientUsd,
      usdRateUsed,
      ilsHelper: {
        baseTotalIls: agentNetIls,
        baggageIlsTotal,
        subtotalIls,
        agentFeeIls: agentFeeInputIls,
        finalClientIls,
      },
    });
  }, [
    hasFlightSelection,
    perPaxUsd,
    pax,
    baseTotalUsd,
    baggageUsdPerPax,
    baggageUsdTotal,
    agentFeeUsd,
    finalClientUsd,
    usdRateUsed,
    agentNetIls,
    baggageIlsTotal,
    subtotalIls,
    agentFeeInputIls,
    finalClientIls,
  ]);

  const rootClass = [
    compact ? "fw-live-summary fw-live-summary--compact" : "fw-live-summary",
    "fw-live-summary--flight-order",
    isProposalReview && !compact ? "fw-live-summary--proposal-review" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const hideOrderSummaryUntilBothSelfComposeLegs =
    currentStep === "selfComposePick" && (!outLegDeal || !inLegDeal);
  const hideDetailsStackOnSelfComposePick = currentStep === "selfComposePick";
  const hasPricedOrderSummary =
    displayPerPaxUsd != null || finalClientUsd != null || ssotHasPayable;
  const showPriceSection =
    (layoutVariant === "full" || layoutVariant === "priceOnly") &&
    !hideOrderSummaryUntilBothSelfComposeLegs &&
    hasPricedOrderSummary;
  const showDetailsSections =
    (layoutVariant === "full" || layoutVariant === "detailsOnly") &&
    !hideDetailsStackOnSelfComposePick;
  const showHeader = showDetailsSections;
  const showCta = showDetailsSections && !hideLiveSummaryCta;

  return (
    <div className={rootClass}>
      {showHeader ? (
        <header className="fos-header" dir="rtl">
          <div className="fos-header__main">
            <p className="fos-header__route" dir="ltr" translate="no">
              {routeCompact}
            </p>
            <p className="fos-header__meta">
              {datesLine} · {effectivePaxForUi} נוסעים
            </p>
          </div>
          <div className="fos-header__side">
            <span className="fos-chip fos-chip--status">הצעת מחיר</span>
            <span className="fos-chip fos-chip--airline" title={airlineMeta.name}>
              {airlineMeta.logo ? (
                <img src={airlineMeta.logo} alt="" aria-hidden className="fos-chip__airline-logo" />
              ) : null}
              <span translate="no">{airlineMeta.code || "—"}</span>
            </span>
          </div>
        </header>
      ) : null}

      {showDetailsSections ? (
        <section className="fos-card" dir="rtl" aria-label="לוח זמנים לטיסות">
          <h3 className="fos-card__title fos-card__title--inline-icon">
            <span className="fos-card__title-icon" aria-hidden>
              <HiOutlinePaperAirplane />
            </span>
            <span className="fos-card__title-text">ציר טיסות</span>
          </h3>
          <div className="fos-timeline-list">
            {bookingTimelineRows.length > 0 ? (
              bookingTimelineRows.map((row) => (
                <article key={row.id} className="fos-timeline-item">
                  <div className="fos-timeline-item__head">
                    <strong>{row.label}</strong>
                    <span>{row.depDateDisplay}</span>
                  </div>
                  <div className="fos-timeline-item__line" dir="rtl" translate="no">
                    <span>
                      [{row.dep}] {row.from}
                    </span>
                    <span className="fos-timeline-item__plane fos-timeline-item__plane--graphic" aria-hidden>
                      <span className="fos-timeline-plane-dash" />
                      <HiOutlinePaperAirplane className="fos-timeline-plane-glyph" />
                      <span className="fos-timeline-plane-dash" />
                    </span>
                    <span>
                      {row.to} [{row.arr}]
                    </span>
                  </div>
                  <div className="fos-timeline-item__meta">
                    <span>{row.stopLabel}</span>
                    <span>משך טיסה: {row.duration}</span>
                  </div>
                </article>
              ))
            ) : (
              <p className="fos-empty">טרם נבחרו פרטי טיסה.</p>
            )}
          </div>
        </section>
      ) : null}

      {showDetailsSections ? (
        <section className="fos-card" dir="rtl" aria-label="פרטי הטיסה">
          <h3 className="fos-card__title fos-card__title--inline-icon">
            <span className="fos-card__title-icon" aria-hidden>
              <HiOutlineShoppingBag />
            </span>
            <span className="fos-card__title-text">פרטי הטיסה</span>
          </h3>
          <div className="fos-details-grid">
            <div className="fos-detail-row">
              <span>כבודה</span>
              <strong>
                {baggageDisplayState.displayState === "included"
                  ? baggageDisplayState.includedLines.join(" · ")
                  : baggageAmount > 0
                    ? formatIlsAmount(Math.ceil(Number(baggageAmount) - 1e-9))
                    : baggageDisplayState.displayState === "no_baggage"
                      ? "אין כבודה כלולה"
                      : "ללא כבודה נוספת"}
              </strong>
            </div>
            <div className="fos-detail-row">
              <span>סוג טיסה</span>
              <strong>{tripBadge || bookingTimelineRows[0]?.stopLabel || "—"}</strong>
            </div>
            <div className="fos-detail-row fos-detail-row--warning">
              <span>תנאי ביטול</span>
              <strong>כפוף לתנאי הספק וחברת התעופה</strong>
            </div>
            {selectedUpgrades.length > 0 ? (
              <div className="fos-detail-row">
                <span>שדרוגים</span>
                <strong>{selectedUpgrades.length} נבחרו</strong>
              </div>
            ) : null}
          </div>
        </section>
      ) : null}

      {showDetailsSections ? (
        <section className="fos-card" dir="rtl" aria-label="נוסעים">
          <h3 className="fos-card__title fos-card__title--inline-icon">
            <span className="fos-card__title-icon" aria-hidden>
              <HiOutlineUser />
            </span>
            <span className="fos-card__title-text">נוסעים</span>
          </h3>
          {passengerRows.length > 0 ? (
            <ul className="fos-passengers">
              {passengerRows.map((row) => (
                <li key={row.id} className="fos-passengers__item">
                  <span className="fos-passengers__name" translate="no">
                    {row.name}
                  </span>
                  <span
                    className={
                      row.complete
                        ? "fos-passengers__status fos-passengers__status--ok"
                        : "fos-passengers__status fos-passengers__status--warn"
                    }
                  >
                    {row.complete ? "פרטים מלאים" : "חסר מידע"}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="fos-empty">טרם מולאו פרטי נוסעים.</p>
          )}
        </section>
      ) : null}

      {showPriceSection ? (
      <section
        className={[
          "fos-card fos-card--price",
          layoutVariant === "priceOnly" ? "fos-card--agent-order-summary" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        dir="rtl"
        aria-label={layoutVariant === "priceOnly" ? "סיכום הזמנה" : "סיכום מחיר"}
      >
        <h3 className="fos-card__title fos-card__title--order-summary">
          {layoutVariant === "priceOnly" ? (
            <>
              <span className="fos-card__title-icon" aria-hidden>
                📋
              </span>
              <span className="fos-card__title-text">סיכום הזמנה</span>
            </>
          ) : (
            <>
              <span className="fos-card__title-icon" aria-hidden>
                💳
              </span>
              <span className="fos-card__title-text">סיכום מחיר</span>
            </>
          )}
        </h3>
        {displayPerPaxUsd != null ? (
          <div className="fos-price-row">
            <span>מחיר לנוסע</span>
            <strong translate="no">
              {formatUsdAmount(Math.ceil(Number(displayPerPaxUsd) - 1e-9))}
            </strong>
          </div>
        ) : null}
        {displayPerPaxUsd != null &&
        (displayBaseIls != null || usdRateUsed != null) ? (
          <div className="fos-price-row fos-price-row--helper">
            <span>מחיר בש"ח</span>
            <strong translate="no">
              {formatIlsHelperAmount(
                displayBaseIls != null
                  ? displayBaseIls / pax
                  : displayPerPaxUsd * usdRateUsed
              )}
            </strong>
          </div>
        ) : null}
        <div className="fos-price-row">
          <span>מספר נוסעים</span>
          <strong translate="no">{`${pax}`}</strong>
        </div>
        {displayBaseTotalUsd != null ? (
          <div className="fos-price-row fos-price-row--base-total">
            <span>סה״כ מחיר לכל הנוסעים</span>
            <strong translate="no">{formatUsdAmount(displayBaseTotalUsd)}</strong>
          </div>
        ) : null}
        {displayBaseIls != null ? (
          <div className="fos-price-row fos-price-row--helper">
            <span>מחיר בש"ח</span>
            <strong translate="no">{formatIlsHelperAmount(displayBaseIls)}</strong>
          </div>
        ) : null}
        {hasBaggageOrExtras ? (
          <>
            <div className="fos-price-row">
              <span>{baggageExtrasLabelHe}</span>
              <strong translate="no">{formatUsdAmount(baggageUsdTotal)}</strong>
            </div>
            {baggageBreakdownLines.map((line, idx) => (
              <div className="fos-price-row fos-price-row--helper" key={`${line.direction}-${line.product}-${idx}`}>
                <span>{`${line.direction}: ${line.product} x${line.qty}`}</span>
                <strong>{line.names.join(", ")}</strong>
              </div>
            ))}
            <div className="fos-price-row fos-price-row--helper">
              <span>מחיר בש"ח</span>
              <strong translate="no">{formatIlsHelperAmount(baggageIlsTotal)}</strong>
            </div>
          </>
        ) : null}
        {displaySubtotalUsd != null ? (
          <div className="fos-price-row">
            <span>Subtotal</span>
            <strong translate="no">{formatUsdAmount(displaySubtotalUsd)}</strong>
          </div>
        ) : null}
        {displaySubtotalIls != null ? (
          <div className="fos-price-row fos-price-row--helper">
            <span>מחיר בש"ח</span>
            <strong translate="no">{formatIlsHelperAmount(displaySubtotalIls)}</strong>
          </div>
        ) : null}
        {hasAgentFee && agentFeeUsd != null ? (
          <>
            <div className="fos-price-row">
              <span>דמי טיפול</span>
              <strong translate="no">{formatUsdAmount(agentFeeUsd)}</strong>
            </div>
            {agentFeeInputIls != null ? (
              <div className="fos-price-row fos-price-row--helper">
                <span>מחיר בש"ח</span>
                <strong translate="no">{formatIlsHelperAmount(agentFeeInputIls)}</strong>
              </div>
            ) : null}
          </>
        ) : null}
        {usdRateUsed != null ? (
          <div className="fos-price-row fos-price-row--helper">
            <span>{`שער דולר (${usdRateCheckedAt})`}</span>
            <strong translate="no">{Number(usdRateUsed).toFixed(4)}</strong>
          </div>
        ) : null}
        {finalClientUsd != null ? (
          <>
            <div className="fos-price-divider" />
            <div className="fos-price-total">
              <span>סכום לתשלום</span>
              <strong translate="no">
                {formatUsdAmount(Math.ceil(Number(finalClientUsd) - 1e-9))}
              </strong>
            </div>
          </>
        ) : null}
        {finalClientIls != null ? (
          <div className="fos-price-row fos-price-row--helper">
            <span>מחיר בש"ח</span>
            <strong translate="no">{formatIlsHelperAmount(finalClientIls)}</strong>
          </div>
        ) : null}
      </section>
      ) : null}

      {showCta ? (
        <section className="fos-cta" dir="rtl">
          <button
            type="button"
            className="fw-live-summary__cta"
            disabled={flowDone || !canContinue}
            onClick={() => nextStep()}
          >
            {flowDone ? "הזמנה הושלמה" : "להמשך לתשלום"}
          </button>
        </section>
      ) : null}
    </div>
  );
}
